import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import {
  IDENTITIES_PAGE_SIZE,
  IDENTITY_SEARCH_DEBOUNCE_MS,
  IDENTITY_SEARCH_MIN_LENGTH,
} from "../constants/identities.constants";
import { IdentitiesService } from "../services/identitiesService";
import type {
  IdentitiesApiResponse,
  IdentitiesCountsResponse,
  IdentitiesFilters,
  Identity,
  IdentityKycGroup,
  IdentityStatus,
  IdentityType,
} from "../types/identities-api.types";

/** `all` means "no bucket filter", so it maps to an absent `kyc_group`. */
export type IdentitiesListFilter = "all" | IdentityKycGroup;

const EMPTY_COUNTS: IdentitiesCountsResponse = { all: 0, approved: 0, pending: 0, rejected: 0 };

// ── pure logic ─────────────────────────────────────────────────────────────
// Split out of the hook so each rule is unit-testable without a DOM: this
// repo's Vitest "unit" project runs in `environment: "node"` with no
// @testing-library/react, so a hook cannot be rendered in a test. Same shape as
// `useStatementPagination`, which exports `chunkStatementTransactions` for the
// same reason.

export interface ResolvedSearchTerm {
  /** Something was typed, but not enough characters to run a search yet. */
  isSearchTermTooShort: boolean;
  /** The term to put on the wire, or `undefined` when it must not be sent. */
  serverSearch: string | undefined;
}

/**
 * The two search signals come from different values on purpose: the hint reacts to what the user is typing
 * right now, while the request waits for the debounced value. Deriving both from one of them would either lag
 * the hint or fire a request per keystroke.
 */
export function resolveSearchTerm(query: string, debouncedQuery: string): ResolvedSearchTerm {
  const trimmed = query.trim();
  return {
    isSearchTermTooShort: trimmed.length > 0 && trimmed.length < IDENTITY_SEARCH_MIN_LENGTH,
    serverSearch: debouncedQuery.length >= IDENTITY_SEARCH_MIN_LENGTH ? debouncedQuery : undefined,
  };
}

/**
 * A chip selection overrides a pinned bucket; `all` falls back to whatever the consumer pinned.
 *
 * No consumer pins one today — the create-card picker used to pin `approved`, and stopped, because hiding a
 * holder it could not issue a card to read as "this person does not exist". The option stays because it maps
 * to a real backend query param a future caller may want; the picker now lists every bucket and disables the
 * rows it cannot offer.
 */
export function resolveKycGroup(
  filter: IdentitiesListFilter,
  fixedKycGroup?: IdentityKycGroup,
): IdentityKycGroup | undefined {
  return filter === "all" ? fixedKycGroup : filter;
}

export interface IdentitiesFiltersInput {
  pageSize: number;
  skip: number;
  withAggregates: boolean;
  fixedType?: IdentityType;
  fixedStatus?: IdentityStatus;
  organizationId?: string;
  kycGroup?: IdentityKycGroup;
  search?: string;
}

/**
 * Every optional param is spread conditionally, never assigned as `undefined`: `buildQueryString` drops
 * `null`/`undefined` but KEEPS empty strings, and the backend runs `forbidNonWhitelisted`, so a stray
 * `?search=` is a 400 rather than an ignored param.
 */
export function buildIdentitiesFilters({
  pageSize,
  skip,
  withAggregates,
  fixedType,
  fixedStatus,
  organizationId,
  kycGroup,
  search,
}: IdentitiesFiltersInput): IdentitiesFilters {
  return {
    limit: pageSize,
    skip,
    ...(withAggregates ? { with_aggregates: true } : {}),
    ...(fixedType ? { type: fixedType } : {}),
    ...(fixedStatus ? { status: fixedStatus } : {}),
    ...(organizationId ? { organization_id: organizationId } : {}),
    ...(kycGroup ? { kyc_group: kycGroup } : {}),
    ...(search ? { search } : {}),
  };
}

export type IdentitiesCountsFiltersInput = Pick<
  IdentitiesFiltersInput,
  "fixedType" | "fixedStatus" | "organizationId" | "search"
>;

/**
 * The counts endpoint takes the listing's filters minus pagination and minus `kyc_group` — each chip has to
 * report its own bucket, so filtering by bucket first would make every count read zero except the active
 * one.
 */
export function buildIdentitiesCountsFilters({
  fixedType,
  fixedStatus,
  organizationId,
  search,
}: IdentitiesCountsFiltersInput): Omit<
  IdentitiesFilters,
  "kyc_group" | "limit" | "skip" | "with_aggregates"
> {
  return {
    ...(fixedType ? { type: fixedType } : {}),
    ...(fixedStatus ? { status: fixedStatus } : {}),
    ...(organizationId ? { organization_id: organizationId } : {}),
    ...(search ? { search } : {}),
  };
}

export interface IdentitiesPageResult {
  list: Identity[];
  total: number;
  hasMore: boolean;
}

/**
 * Reads one page out of the response envelope defensively, and derives `hasMore` from the rows actually
 * returned when the server does not report it: the server computes `skip + limit < total`, which overshoots
 * on a short last page and would leave a "load more" button that fetches nothing.
 */
export function readIdentitiesPage(
  data: IdentitiesApiResponse | undefined,
  skip: number,
): IdentitiesPageResult {
  const list: Identity[] = Array.isArray(data?.data) ? data.data : [];
  const total = typeof data?.total === "number" ? data.total : list.length;
  const hasMore = typeof data?.hasMore === "boolean" ? data.hasMore : skip + list.length < total;
  return { list, total, hasMore };
}

export interface RequestSequence {
  /** Claims the next number, for a request about to start. */
  next: () => number;
  /** True only for the most recently claimed number; everything else is stale. */
  isCurrent: (seq: number) => boolean;
}

/**
 * Guards against out-of-order responses: a slow request that resolves after a newer one must not write its
 * rows, its error, or its loading flags. Extracted so that rule is verifiable without rendering the hook.
 */
export function createRequestSequence(): RequestSequence {
  let current = 0;
  return {
    next: () => ++current,
    isCurrent: (seq: number) => seq === current,
  };
}

export interface UseIdentitiesListOptions {
  /** Message shown when the request fails and the server sent no usable one. */
  loadErrorFallback: string;
  /** Hold every request back until true — e.g. a modal that has not opened yet. */
  enabled?: boolean;
  pageSize?: number;
  /** Ask for `cards_count` per row. */
  withAggregates?: boolean;
  /** Fetch the per-bucket counts. Skip it when the consumer renders no chips. */
  withCounts?: boolean;
  /**
   * Filters the consumer pins for the whole surface, as flat primitives rather than an options object on
   * purpose: an inline `{ status: "ACTIVE" }` would be a new reference on every render and re-fire the load
   * effect forever.
   */
  fixedStatus?: IdentityStatus;
  fixedKycGroup?: IdentityKycGroup;
  fixedType?: IdentityType;
  organizationId?: string;
  /** Seed values, for a surface that restores its filters from the URL. Read once. */
  initialQuery?: string;
  initialFilter?: IdentitiesListFilter;
}

/**
 * The identities listing: one request per filter change, all filtering done by the backend.
 *
 * This replaces filtering in the browser over whatever rows happened to be loaded — which could not see
 * anything past the current page, and which made the "load more" button disappear the moment a term was
 * typed. Every filter here is a query param, and because they are all dependencies of the load callback,
 * changing any one of them re-issues the request from `skip: 0`. There is exactly one fetch implementation,
 * used by both the initial load and "load more".
 */
export function useIdentitiesList({
  loadErrorFallback,
  enabled = true,
  pageSize = IDENTITIES_PAGE_SIZE,
  withAggregates = false,
  withCounts = false,
  fixedStatus,
  fixedKycGroup,
  fixedType,
  organizationId,
  initialQuery = "",
  initialFilter = "all",
}: UseIdentitiesListOptions) {
  const [identities, setIdentities] = useState<Identity[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState<IdentitiesCountsResponse>(EMPTY_COUNTS);

  const [filter, setFilter] = useState<IdentitiesListFilter>(initialFilter);
  const [query, setQuery] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(() => initialQuery.trim());
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), IDENTITY_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const { isSearchTermTooShort, serverSearch } = resolveSearchTerm(query, debouncedQuery);
  const activeKycGroup = resolveKycGroup(filter, fixedKycGroup);
  const hasActiveFilters = Boolean(serverSearch) || filter !== "all";

  // Drops stale responses when filters change mid-flight.
  const requestSeqRef = useRef<RequestSequence>(createRequestSequence());

  const fetchPage = useCallback(
    async (append: boolean, skip: number) => {
      const sequence = requestSeqRef.current;
      const currentSeq = sequence.next();
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setError(null);
      }

      try {
        const response = await IdentitiesService.fetchIdentities(
          buildIdentitiesFilters({
            pageSize,
            skip,
            withAggregates,
            fixedType,
            fixedStatus,
            organizationId,
            kycGroup: activeKycGroup,
            search: serverSearch,
          }),
        );
        if (!sequence.isCurrent(currentSeq)) return;

        const {
          list,
          total: nextTotal,
          hasMore: nextHasMore,
        } = readIdentitiesPage(response.data as IdentitiesApiResponse, skip);

        setIdentities((prev) => (append ? [...prev, ...list] : list));
        setTotal(nextTotal);
        setHasMore(nextHasMore);
      } catch (err: unknown) {
        if (!sequence.isCurrent(currentSeq)) return;
        setError(HttpApiService.getErrorMessage(err) || loadErrorFallback);
        if (!append) {
          setIdentities([]);
          setTotal(0);
          setHasMore(false);
        }
      } finally {
        if (sequence.isCurrent(currentSeq)) {
          setIsLoading(false);
          setIsLoadingMore(false);
          setHasLoaded(true);
        }
      }
    },
    [
      pageSize,
      withAggregates,
      fixedType,
      fixedStatus,
      organizationId,
      activeKycGroup,
      serverSearch,
      loadErrorFallback,
    ],
  );

  // `fetchPage` changes whenever a filter changes, so this reloads from the
  // first page — no explicit "reset pagination" step to forget. `reloadNonce`
  // covers the one case the filters cannot express: refetching with the exact
  // same filters, after a holder was created.
  useEffect(() => {
    if (!enabled) return;
    void fetchPage(false, 0);
  }, [enabled, fetchPage, reloadNonce]);

  useEffect(() => {
    if (!enabled || !withCounts) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await IdentitiesService.fetchIdentitiesCounts(
          buildIdentitiesCountsFilters({
            fixedType,
            fixedStatus,
            organizationId,
            search: serverSearch,
          }),
        );
        if (!cancelled) setCounts(response.data);
      } catch {
        // Chips fall back to 0; the listing itself still works.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, withCounts, fixedType, fixedStatus, organizationId, serverSearch]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    void fetchPage(true, identities.length);
  }, [isLoadingMore, hasMore, fetchPage, identities.length]);

  /**
   * Clears the filters and reloads — used after creating a holder. The nonce is what guarantees a refetch
   * even when the filters were already clear, and React batches all four updates into a single effect run.
   */
  const reload = useCallback(() => {
    setQuery("");
    setDebouncedQuery("");
    setFilter("all");
    setReloadNonce((nonce) => nonce + 1);
  }, []);

  return useMemo(
    () => ({
      identities,
      total,
      counts,
      hasMore,
      isLoading,
      isLoadingMore,
      hasLoaded,
      error,
      query,
      setQuery,
      isSearchTermTooShort,
      hasActiveFilters,
      filter,
      setFilter,
      loadMore,
      reload,
    }),
    [
      identities,
      total,
      counts,
      hasMore,
      isLoading,
      isLoadingMore,
      hasLoaded,
      error,
      query,
      isSearchTermTooShort,
      hasActiveFilters,
      filter,
      loadMore,
      reload,
    ],
  );
}
